import { Product } from "@/api/product-api";
import StickyUptoFooter from "@/components/StickyUptoFooter";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import { useEvent } from "@/hooks/useEvent";
import { useObserver } from "@/hooks/useObserver";
import { cn } from "@/libs/utils";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/v2-components/ui/select";
import TactSuitCard from "@/v2-components/TactSuitCard";
import { useCallback, useRef, useState } from "react";

interface Props {
  selected: Product;
  products: Product[];
  onValueChange: (index: number, value: string) => void;
  index: number;
}

export function ProductSelect({
  selected,
  products,
  onValueChange,
  index,
}: Props) {
  const [offset, setOffset] = useState(0);
  const gnbRef = useRef<HTMLElement | null>(null);
  const isMobileView = useIsMobileView();

  const onHandle = useCallback(() => {
    const domRect = gnbRef.current?.getBoundingClientRect();
    const height = domRect?.height ?? 0;
    const top = domRect?.top ?? 0;
    const offset = top + height + 10 + (isMobileView ? 48 : 60); // 48, 60은 SubMenu 높이
    setOffset((state) => (Math.abs(offset - state) > 1 ? offset : state));
  }, [isMobileView]);

  useEvent("scroll", onHandle, true);
  useObserver(gnbRef, onHandle);

  return (
    <div
      data-index={index}
      className="mb-7 flex w-full select-none flex-col items-center data-[index=2]:hidden laptop:mb-24 laptop:data-[index=2]:flex"
    >
      <StickyUptoFooter className="w-full" offset={offset}>
        <Select onValueChange={(value) => onValueChange(index, value)}>
          <SelectTrigger
            data-index={index}
            className={cn(
              "h-[60px] w-full data-[index=2]:hidden laptop:data-[index=2]:flex",
            )}
            variant="form"
          >
            <SelectValue placeholder={selected?.name ?? ""} />
            <ChevronDownIcon />
          </SelectTrigger>
          <SelectContent>
            {products.map((p) => (
              <SelectItem key={p.slug} value={p.slug}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </StickyUptoFooter>
      <TactSuitCard product={selected}>
        <div className="flex flex-col items-center">
          <div
            className={
              "relative my-3 flex aspect-square w-full items-center justify-center laptop:w-[240px]"
            }
          >
            <TactSuitCard.Thumbnail fill />
          </div>
          <div className="mb-3 h-[28px] laptop:h-[32px]">
            <TactSuitCard.MetaBadge className="h-full w-[127px] laptop:w-[145px]" />
          </div>
          <TactSuitCard.Price className="text-center text-2xl font-400" />
          <TactSuitCard.LearnMore className="mt-3 pl-4" />
        </div>
      </TactSuitCard>
    </div>
  );
}
